import {LoadingOutlined} from "@ant-design/icons";
import {Alert, Button, Menu, message, Space, Typography} from "antd";
import DropdownButton from "antd/lib/dropdown/dropdown-button";
import React, {FC, useState} from "react";
import Icon from "../../../../assets/icons/icon";
import {renderErrorMessage} from "../../../../components/messages/errorMessage";
import ServerSelectBox from "../../../../components/serverSelectBox";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import _t from "../../../../lang/translate";
import {billingDestinationsAutoComplete} from "../../../../services/autocompleteService";
import {openFileLink} from "../../../../services/mediaService";
import {saveTaxDraftInvoice, sentTaxInvoice, sentTaxInvoiceAgain} from "../../../../services/taxService";
import {TaxCase, TaxCaseValueConfigItem} from "../../../../types/taxTypes";
import {wasPaidWithInvoice} from "../../../../utilities/paymentTypeHelpers";
import {isValidNum} from "../../../../utilities/typeGuard";

interface TaxInvoiceActionsProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  dirty?: boolean;
  disabled?: boolean
}
function showMessageIfDealerHasCredit(paymentTermCode: TaxCaseValueConfigItem | null | undefined) {
  if ([1, 2, 4, 5, 6, 8].includes(Number(paymentTermCode?.value))) {
    message.info(_t("msg.userHasCredit"))
  }
}
function checkCanSendInvoice(taxCase: TaxCase, dirty?: boolean) {
  if (dirty) return false;
  const taxFlow = taxCase.scenarios.optimization?.calculation
  if (!isValidNum(taxFlow?.registrationFee?.registrationFee)) return false;
  if (taxCase.vehicle?.isNew) {
    return !!taxFlow?.newPrice?.taxableValue
  } else {
    return !!taxFlow?.newPrice?.newPrice && !!taxFlow?.tradePrice?.tradePrice
  }
}
const TaxInvoiceActions: FC<TaxInvoiceActionsProps> = ({ setLoading, loading, dirty, disabled }) => {
  const { taxCase, setTaxCase, isAdmin } = TaxCaseStore.useContainer();
  const invoiceIsSent = !!taxCase.links?.invoiceLink;
  const invoiceIsGenerating = !!taxCase.invoiceStartedAt && !invoiceIsSent;
  const canSendInvoice = checkCanSendInvoice(taxCase, dirty)
  const disableApplied = disabled && !invoiceIsSent


  const initialBillingDestionation = taxCase.billingDestinationId || -1;
  const [billingDestinationId, setBillingDestinationId] = useState<number>(initialBillingDestionation);

  const sendInvoice = async () => {
    try {
      setLoading(true);
      const { data } = await sentTaxInvoice(taxCase.id, billingDestinationId);
      setTaxCase(data);
      message.success(_t("invoice", "send"));
      showMessageIfDealerHasCredit(taxCase.taxCaseValueConfig?.paymentTermCode)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false);
    }
  };

  const sendInvoiceAgain = async () => {
    try {
      setLoading(true);
      await sentTaxInvoiceAgain(taxCase.id);
      message.success(_t("invoice", "send"));
      showMessageIfDealerHasCredit(taxCase.taxCaseValueConfig?.paymentTermCode)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false);
    }
  };

  const sendInvoiceDraft = async () => {
    try {
      setLoading(true);
      await saveTaxDraftInvoice(taxCase.id, billingDestinationId);
      message.success(_t("invoice", "saved"));
      showMessageIfDealerHasCredit(taxCase.taxCaseValueConfig?.paymentTermCode)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false);
    }
  };


  const getInvoiceBtnLabel = () => {
    if (invoiceIsGenerating)
      return (
        <>
          <LoadingOutlined />
          &nbsp; &nbsp;
          {_t("msg.generating_invoice")}...
        </>
      );

    if (invoiceIsSent) return _t("resend_invoice");

    return _t("send", "invoice");
  };

  const invoiceMenu = (
    <Menu>
      <Menu.Item disabled={invoiceIsGenerating || !canSendInvoice || disabled}
        key="1" onClick={sendInvoiceDraft}
      >
        {_t("create_invoice_draft")}
      </Menu.Item>
      <Menu.Item disabled={invoiceIsGenerating || !canSendInvoice || disabled}
        key="2" onClick={sendInvoice}
      >
        {_t("send", "invoice", " new ")}
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Typography.Title level={5}>{_t("invoice")}</Typography.Title>
      {taxCase.client?.typeId !== 2 && (
        <div>
          <ServerSelectBox
            disabled={disabled}
            className="w-100 mb-1"
            value={billingDestinationId}
            onChange={(v) => setBillingDestinationId(Array.isArray(v) ? -1 : v)}
            addModalType="addBillingDestination"
            onItemAdded={(_, id) => typeof id === "number" && setBillingDestinationId(id)}
            addModalExtraValues={{ parentId: taxCase.client?.dealerId }}
            placeholder={_t("billing_destination")}
            filters={`filter[dealerId]=${taxCase.client?.dealerId}`}
            apiUrl={billingDestinationsAutoComplete()}
          />
        </div>
      )}
      <div className="text-right">
        <Space>
          <Button
            type="primary"
            disabled={!taxCase.links?.invoiceLink}
            ghost
            onClick={() => openFileLink(taxCase.links?.invoiceLink)}
          >
            <Icon name="download-outline" size="large" /> {_t("invoice")}
          </Button>
          <DropdownButton
            type="primary"
            onClick={invoiceIsSent ? sendInvoiceAgain : sendInvoice}
            disabled={invoiceIsGenerating || !canSendInvoice || disableApplied}
            overlay={invoiceMenu}
          >
            {getInvoiceBtnLabel()}
          </DropdownButton>
        </Space>
      </div>
      {wasPaidWithInvoice(taxCase.paymentDetails) && isAdmin && (
        <Alert className="mt-2" message={<>
          {taxCase.taxCaseValueConfig?.paymentTermCode.title}
          :
          &nbsp;
          {
            taxCase.taxCaseValueConfig?.paymentTermCode.options?.find((item) => {
              return item.id === taxCase.taxCaseValueConfig?.paymentTermCode?.value
            })?.title
          }
        </>}
        />
      )}

    </>
  );
};

export default TaxInvoiceActions;
